import { useRef } from 'react';
import { PiTrashDuotone } from 'react-icons/pi';

import { useGraph } from '@spazious/dollhouse-store';
import { Flex, IconButton, InputText, Typography } from '@spazious/dollhouse-uikit';
import { useEventCallback } from '@spazious/react-hooks';

import { useMetaFields, useSelectedNodes } from '../../hooks/index.js';

import { Grid } from './Grid.js';

const focussableElements =
  'a:not([disabled]), button:not([disabled]), input[type=text]:not([disabled]), [tabindex]:not([disabled]):not([tabindex="-1"])';

function focusNextElement(elem: HTMLFormElement, container: HTMLElement) {
  //add all elements we want to include in our selection
  var focussable = Array.prototype.filter.call(
    container.querySelectorAll(focussableElements),
    function (element) {
      //check for visibility while always include the current activeElement
      return (
        element.offsetWidth > 0 || element.offsetHeight > 0 || element === document.activeElement
      );
    }
  );
  var index = focussable.indexOf(document.activeElement);

  if (index > -1) {
    var nextElement = focussable[index + 1] || focussable[0];
    nextElement.focus();
  }
}

export interface CustomMetaFieldsProps {
  multiple?: boolean;
  ignore?: string[];
  disabled?: boolean;
}

export const CustomMetaFields: React.FC<CustomMetaFieldsProps> = ({
  multiple = true,
  disabled = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { graph } = useGraph();
  const selected = useSelectedNodes();
  const selectedShallowNodes = selected.map((node) => graph.shallow.nodes[node.selector!]);

  const {
    editField,
    globalFields,
    metaFieldValues,
    onChangeField,
    onEditField,
    onRemoveField,
    onSubmitField,
  } = useMetaFields();

  const handleKeyDown = useEventCallback((evt: React.KeyboardEvent<HTMLInputElement>) => {
    if ((evt.key === 'Enter' || evt.key === 'Tab') && containerRef.current) {
      evt.preventDefault();
      focusNextElement(evt.target as any, containerRef.current);
    } else if (evt.key === 'Escape') {
      onEditField(-1);
    }
  });

  if (multiple === false && selectedShallowNodes.length > 1) {
    return null;
  }

  return (
    <Flex ref={containerRef} column gap="calc(var(--spacing-gap) * 2)">
      {globalFields.map((fieldName, idx) => {
        return (
          <Grid key={fieldName}>
            {editField === idx ? (
              <InputText
                key={fieldName}
                autoFocus
                defaultValue={fieldName}
                placeholder="Field name"
                style={{ width: '125px' }}
                onBlur={onSubmitField}
                onFocus={(evt) => evt.target.select()}
                onKeyDown={handleKeyDown}
              />
            ) : (
              <Flex key={fieldName} alignItems="center">
                <Typography size="large" variant="label" onDoubleClick={() => onEditField(idx)}>
                  {fieldName}:{' '}
                </Typography>

                {globalFields.includes(fieldName) && (
                  <IconButton onClick={() => onRemoveField(fieldName)}>
                    <PiTrashDuotone size="20px" />
                  </IconButton>
                )}
              </Flex>
            )}

            <InputText
              disabled={disabled || editField === null}
              value={metaFieldValues[fieldName] ?? ''}
              onChange={(evt) => {
                onChangeField(fieldName, evt.target.value);
              }}
            />
          </Grid>
        );
      })}
    </Flex>
  );
};
